import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401.
// Concurrent 401s share a single in-flight refresh promise so that every
// queued request waits for the same refresh to finish and then retries with
// the new token. Without this, a cold page load that fires N parallel
// requests with an expired token would have the first request refresh and
// succeed while the other N-1 requests bailed out (because `isRefreshing`
// was true) and rejected — the historical 'articles vanish on refresh' bug.
let refreshPromise = null;

function doRefresh() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.reject(new Error('no refresh token'));
  refreshPromise = axios
    .post(`${API_BASE}/api/auth/refresh`, { refresh_token: refreshToken })
    .then((res) => {
      const { access_token, refresh_token: newRefresh } = res.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefresh);
      return access_token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/')
    ) {
      originalRequest._retry = true;
      try {
        const newToken = await doRefresh();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        // Refresh failed — fall through to the below handling.
      }
      if (window.location.pathname.startsWith('/admin')) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authAPI = {
  login: (data) => api.post('/api/auth/login', data),
  mfaLoginVerify: (data) => api.post('/api/auth/mfa/login-verify', data),
  mfaSetupComplete: (data) => api.post('/api/auth/mfa/setup-complete', data),
  register: (data) => api.post('/api/auth/register', data),
  me: () => api.get('/api/auth/me'),
  refresh: (token) => api.post('/api/auth/refresh', { refresh_token: token }),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  updateEmail: (data) => api.put('/api/auth/email', data),
  changePassword: (data) => api.put('/api/auth/change-password', data),
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/auth/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Articles
export const articlesAPI = {
  list: (params) => api.get('/api/articles', { params }),
  get: (id) => api.get(`/api/articles/${id}`),
  getBySlug: (slug) => api.get(`/api/articles/by-slug/${encodeURIComponent(slug)}`),
  create: (data) => api.post('/api/articles', data),
  update: (id, data) => api.put(`/api/articles/${id}`, data),
  delete: (id) => api.delete(`/api/articles/${id}`),
  workflow: (id, data) => api.post(`/api/articles/${id}/workflow`, data),
  updateSEO: (id, data) => api.put(`/api/articles/${id}/seo`, data),
  revisions: (id) => api.get(`/api/articles/${id}/revisions`),
  bulk: (data) => api.post('/api/articles/bulk', data),
};

// Categories
export const categoriesAPI = {
  list: () => api.get('/api/categories'),
  create: (data) => api.post('/api/categories', data),
  update: (id, data) => api.put(`/api/categories/${id}`, data),
  delete: (id) => api.delete(`/api/categories/${id}`),
  tree: () => api.get('/api/categories/tree'),
  setParent: (id, parentId) => api.put(`/api/categories/${id}/parent`, { parent_id: parentId }),
};

// RSS Feeds
export const rssAPI = {
  list: () => api.get('/api/rss-feeds'),
  create: (data) => api.post('/api/rss-feeds', data),
  fetch: (id) => api.post(`/api/rss-feeds/${id}/fetch`),
  import: (id, items) => api.post(`/api/rss-feeds/${id}/import`, { items }),
  update: (id, data) => api.put(`/api/rss-feeds/${id}`, data),
  delete: (id) => api.delete(`/api/rss-feeds/${id}`),
};

// Breaking News
export const breakingAPI = {
  list: (activeOnly = true) => api.get('/api/breaking-news', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/api/breaking-news', data),
  update: (id, data) => api.put(`/api/breaking-news/${id}`, data),
  delete: (id) => api.delete(`/api/breaking-news/${id}`),
};

// Comments
export const commentsAPI = {
  list: (articleId, params) => api.get(`/api/articles/${articleId}/comments`, { params }),
  create: (articleId, data) => api.post(`/api/articles/${articleId}/comments`, data),
  update: (id, data) => api.put(`/api/comments/${id}`, data),
  delete: (id) => api.delete(`/api/comments/${id}`),
  report: (id, reason) => api.post(`/api/comments/${id}/report`, { reason }),
  like: (id) => api.post(`/api/comments/${id}/like`),
  dislike: (id) => api.post(`/api/comments/${id}/dislike`),
  moderation: (params) => api.get('/api/comments/moderation', { params }),
};

// AI
export const aiAPI = {
  generate: (data) => api.post('/api/ai/generate', data),
  summarize: (data) => api.post('/api/ai/summarize', data),
  categorize: (data) => api.post('/api/ai/categorize', data),
};

// Newsletter
export const subscriptionsAPI = {
  list: (params) => api.get('/api/subscriptions', { params }),
  subscribe: (data) => api.post('/api/subscriptions', data),
  unsubscribe: (email) => api.delete(`/api/subscriptions/${email}`),
};

// Analytics
export const analyticsAPI = {
  overview: (days) => api.get('/api/analytics/overview', { params: { days } }),
  recordEvent: (data) => api.post('/api/analytics/event', data),
  authorAnalytics: (authorId, days) => api.get(`/api/analytics/authors/${authorId}`, { params: { days } }),
  articleAnalytics: (articleId) => api.get(`/api/analytics/articles/${articleId}`),
  contentPerformance: (days) => api.get('/api/analytics/content-performance', { params: { days } }),
  // Composite per-article performance pane (views, progress, paywall funnel,
  // device split) used by the editor dashboard.
  articlePerformance: (articleId, days) =>
    api.get(`/api/analytics/articles/${articleId}/performance`, { params: { days } }),
  // Whole-site subscription funnel for the admin dashboard.
  subscriptionFunnel: (days) =>
    api.get('/api/analytics/subscription-funnel', { params: { days } }),
  // Ranked article leaderboard (views/unique readers/completion/etc.)
  topArticles: (days, limit = 20, sort = 'views') =>
    api.get('/api/analytics/top-articles', { params: { days, limit, sort } }),
  // Log a paywall lifecycle event (shown / cta_clicked / converted).
  paywallEvent: (data) => api.post('/api/analytics/paywall-event', data),
};

// Auto-publisher — RSS → top N per category → live articles.
// Admin-only; all calls require the `manage_rss` permission.
export const autoPublishAPI = {
  getConfig: () => api.get('/api/auto-publish/config'),
  updateConfig: (patch) => api.put('/api/auto-publish/config', patch),
  listFeeds: (categorySlug) =>
    api.get('/api/auto-publish/feeds', { params: categorySlug ? { category_slug: categorySlug } : {} }),
  addFeed: (data) => api.post('/api/auto-publish/feeds', data),
  toggleFeed: (feedId, isActive) =>
    api.patch(`/api/auto-publish/feeds/${feedId}`, { is_active: isActive }),
  deleteFeed: (feedId) => api.delete(`/api/auto-publish/feeds/${feedId}`),
  seedDefaults: () => api.post('/api/auto-publish/seed-defaults'),
  runNow: () => api.post('/api/auto-publish/run'),
  recentRuns: (limit = 20) => api.get('/api/auto-publish/runs', { params: { limit } }),
};

// Search
export const searchAPI = {
  search: (q, params) => api.get('/api/search', { params: { q, ...params } }),
};

// Public distribution (no auth required; works for anonymous readers)
export const publicAPI = {
  editions: () => api.get('/api/public/editions'),
  homeLayout: () => api.get('/api/public/home-layout'),
  articles: (params) => api.get('/api/public/articles', { params }),
  widgetArticles: (params) => api.get('/api/public/widget/articles', { params }),
};

// Users
export const usersAPI = {
  list: (params) => api.get('/api/users', { params }),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  delete: (id) => api.delete(`/api/users/${id}`),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/api/dashboard/stats'),
};

// Tags
export const tagsAPI = {
  list: () => api.get('/api/tags'),
};

// ========== ENTERPRISE APIs ==========

// Editorial Calendar
export const calendarAPI = {
  list: (params) => api.get('/api/editorial/calendar', { params }),
  create: (data) => api.post('/api/editorial/calendar', data),
  update: (id, data) => api.put(`/api/editorial/calendar/${id}`, data),
  delete: (id) => api.delete(`/api/editorial/calendar/${id}`),
};

// HR / Staff
export const hrAPI = {
  departments: () => api.get('/api/hr/departments'),
  createDepartment: (data) => api.post('/api/hr/departments', data),
  updateDepartment: (id, data) => api.put(`/api/hr/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/api/hr/departments/${id}`),
  staff: (params) => api.get('/api/hr/staff', { params }),
  updateStaff: (id, data) => api.put(`/api/hr/staff/${id}`, data),
  activity: (params) => api.get('/api/hr/activity', { params }),
};

// Media Library
export const mediaAPI = {
  list: (params) => api.get('/api/media', { params }),
  create: (data) => api.post('/api/media', data),
  update: (id, data) => api.put(`/api/media/${id}`, data),
  delete: (id) => api.delete(`/api/media/${id}`),
  upload: (formData) => api.post('/api/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getVersions: (id) => api.get(`/api/media/${id}/versions`),
  saveVersion: (id) => api.post(`/api/media/${id}/versions`),
  updateRights: (id, data) => api.put(`/api/media/${id}/rights`, data),
  aiTag: (id) => api.post(`/api/media/${id}/ai-tag`),
  applyTags: (id, tags) => api.post(`/api/media/${id}/apply-tags`, { tags }),
};

// Templates
export const templatesAPI = {
  list: () => api.get('/api/templates'),
  create: (data) => api.post('/api/templates', data),
  update: (id, data) => api.put(`/api/templates/${id}`, data),
  delete: (id) => api.delete(`/api/templates/${id}`),
};

// Audit Logs
export const auditAPI = {
  list: (params) => api.get('/api/audit', { params }),
};

// Notifications
export const notificationsAPI = {
  list: (params) => api.get('/api/notifications', { params }),
  markRead: (ids) => api.post('/api/notifications/read', { ids }),
  delete: (id) => api.delete(`/api/notifications/${id}`),
};

// Live Blog
export const liveBlogAPI = {
  list: (params) => api.get('/api/live', { params }),
  get: (id) => api.get(`/api/live/${id}`),
  create: (data) => api.post('/api/live', data),
  update: (id, data) => api.put(`/api/live/${id}`, data),
  delete: (id) => api.delete(`/api/live/${id}`),
  postUpdate: (id, data) => api.post(`/api/live/${id}/updates`, data),
};

// Polls
export const pollsAPI = {
  list: (params) => api.get('/api/polls', { params }),
  get: (id) => api.get(`/api/polls/${id}`),
  create: (data) => api.post('/api/polls', data),
  update: (id, data) => api.put(`/api/polls/${id}`, data),
  delete: (id) => api.delete(`/api/polls/${id}`),
  vote: (id, data) => api.post(`/api/polls/${id}/vote`, data),
};

// Bookmarks
export const bookmarksAPI = {
  list: (params) => api.get('/api/bookmarks', { params }),
  add: (data) => api.post('/api/bookmarks', data),
  remove: (articleId) => api.delete(`/api/bookmarks/${articleId}`),
};

// Metered paywall
export const meteredAPI = {
  recordView: (articleId) => api.post(`/api/metered/view/${articleId}`),
  getStatus: () => api.get('/api/metered/status'),
};

// Gift subscriptions
export const giftAPI = {
  create: (data) => api.post('/api/subscriptions/gift', data),
  redeem: (code) => api.post('/api/subscriptions/redeem-gift', { gift_code: code }),
  analytics: () => api.get('/api/subscriptions/analytics'),
};

// Internal Wiki
export const wikiAPI = {
  list: (params) => api.get('/api/wiki', { params }),
  get: (id) => api.get(`/api/wiki/${id}`),
  create: (data) => api.post('/api/wiki', data),
  update: (id, data) => api.put(`/api/wiki/${id}`, data),
  delete: (id) => api.delete(`/api/wiki/${id}`),
};

// Fact-checking
export const factCheckAPI = {
  get: (articleId) => api.get(`/api/articles/${articleId}/fact-checks`),
  update: (articleId, data) => api.put(`/api/articles/${articleId}/fact-checks`, data),
};

// Live Blog extras
export const liveBlogExtrasAPI = {
  summarize: (id) => api.post(`/api/live/${id}/summarize`),
  convertToArticle: (id, data) => api.post(`/api/live/${id}/convert-to-article`, data),
};

// Breaking news extras
export const breakingExtrasAPI = {
  autoPromote: (id) => api.post(`/api/breaking-news/${id}/auto-promote`),
  getAlertFatigue: () => api.get('/api/push/alert-fatigue'),
  setAlertFatigue: (data) => api.put('/api/push/alert-fatigue', data),
};

// Web Vitals
export const vitalsAPI = {
  record: (data) => api.post('/api/analytics/web-vitals', data),
  get: (days) => api.get('/api/analytics/web-vitals', { params: { days } }),
};

// Geo/Device analytics
export const geoDeviceAPI = {
  get: (days) => api.get('/api/analytics/geo-device', { params: { days } }),
  optimalTime: (days) => api.get('/api/analytics/optimal-publish-time', { params: { days } }),
};

// Internal links + autocomplete
export const internalLinksAPI = {
  suggest: (articleId) => api.get(`/api/articles/${articleId}/internal-links`),
};

export const autocompleteAPI = {
  search: (q) => api.get('/api/search/autocomplete', { params: { q } }),
};

// MFA
export const mfaAPI = {
  setup: () => api.post('/api/auth/mfa/setup'),
  verify: (code) => api.post('/api/auth/mfa/verify', { code }),
  disable: (code) => api.post('/api/auth/mfa/disable', { code }),
};

// Bradford Factor + Leave accrual
export const hrAdvancedAPI = {
  bradfordFactor: () => api.get('/api/hr/bradford-factor'),
  getLeaveAccrual: () => api.get('/api/hr/leave-accrual'),
  setLeaveAccrual: (userId, data) => api.put(`/api/hr/leave-accrual/${userId}`, data),
};

// i18n
export const i18nAPI = {
  getLanguages: () => api.get('/api/i18n/languages'),
  setLanguages: (data) => api.put('/api/i18n/languages', data),
};

// Verified badges
export const badgeAPI = {
  setVerified: (userId, isVerified) => api.put(`/api/users/${userId}/verified`, { is_verified: isVerified }),
};

// Comment rate check
export const commentRateAPI = {
  check: (articleId) => api.get(`/api/articles/${articleId}/comments/rate-check`),
};

// Podcast RSS
export const podcastAPI = {
  rss: () => api.get('/api/podcast/rss'),
};

// Video & Audio — reels, live streams and podcast episodes shown on the site and in the apps.
// is_active false = draft; `list(false)` includes drafts (staff only).
const mediaCollectionAPI = (path, key) => ({
  list: (activeOnly = true) =>
    api.get(`/api/${path}`, { params: { active_only: activeOnly, limit: 100 } }).then((res) => res.data[key] || []),
  create: (data) => api.post(`/api/${path}`, data),
  update: (id, data) => api.put(`/api/${path}/${id}`, data),
  delete: (id) => api.delete(`/api/${path}/${id}`),
});
export const reelsAPI = mediaCollectionAPI('reels', 'reels');
export const streamsAPI = mediaCollectionAPI('streams', 'streams');
export const podcastEpisodesAPI = mediaCollectionAPI('podcasts', 'podcasts');

// Webhook dispatch
export const webhookDispatchAPI = {
  dispatch: (payload) => api.post('/api/webhooks/dispatch', { payload }),
};

// Newsletter builder
export const newsletterBuilderAPI = {
  build: (data) => api.post('/api/newsletter/build', data),
  list: () => api.get('/api/newsletter/builds'),
  send: (id) => api.post(`/api/newsletter/builds/${id}/send`),
};

// Billing / Payments
export const billingAPI = {
  prices: () => api.get('/api/payments/prices'),
  userTier: () => api.get('/api/subscriptions/user-tier'),
  history: () => api.get('/api/payments/history'),
  portal: (returnUrl) => api.post('/api/payments/portal', { return_url: returnUrl || window.location.href }),
  checkout: (packageId) => api.post('/api/payments/checkout', { package_id: packageId }),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/api/settings'),
  update: (data) => api.put('/api/settings', data),
};

// API Keys (admin)
export const apiKeysAPI = {
  get: () => api.get('/api/admin/api-keys'),
  update: (keys) => api.put('/api/admin/api-keys', { keys }),
};

// App Config (review mode toggle)
export const appConfigAPI = {
  get: () => api.get('/api/public/app-config'),
  update: (data) => api.put('/api/app-config', data),
};

// Ads
export const adsAPI = {
  list: (params) => api.get('/api/ads', { params }),
  create: (data) => api.post('/api/ads', data),
  update: (id, data) => api.put(`/api/ads/${id}`, data),
  delete: (id) => api.delete(`/api/ads/${id}`),
};

// Export
export const exportAPI = {
  articles: (params) => api.get('/api/export/articles', { params }),
};

// ========== V2 ENTERPRISE APIs ==========

// Enhanced Workflow
export const workflowAPI = {
  stages: () => api.get('/api/workflow/stages'),
  action: (articleId, data) => api.post(`/api/articles/${articleId}/workflow-v2`, data),
  setPriority: (articleId, data) => api.put(`/api/articles/${articleId}/priority`, data),
  setEmbargo: (articleId, data) => api.put(`/api/articles/${articleId}/embargo`, data),
  kill: (articleId, data) => api.post(`/api/articles/${articleId}/kill`, data),
  addCorrection: (articleId, data) => api.post(`/api/articles/${articleId}/correction`, data),
};

// Version History
export const versionsAPI = {
  list: (articleId) => api.get(`/api/articles/${articleId}/versions`),
  save: (articleId) => api.post(`/api/articles/${articleId}/save-version`),
  diff: (articleId, v1, v2) => api.get(`/api/articles/${articleId}/diff`, { params: { v1, v2 } }),
  rollback: (articleId, version) => api.post(`/api/articles/${articleId}/rollback/${version}`),
  lock: (articleId) => api.post(`/api/articles/${articleId}/lock`),
  unlock: (articleId) => api.post(`/api/articles/${articleId}/unlock`),
};

// Peer approvals
export const peerApproveAPI = {
  approve: (articleId, data) => api.post(`/api/articles/${articleId}/peer-approve`, data),
  setRequired: (articleId, n) => api.put(`/api/articles/${articleId}/required-approvals`, { required_approvals: n }),
};

// Leave Management
export const leaveAPI = {
  list: (params) => api.get('/api/hr/leave', { params }),
  create: (data) => api.post('/api/hr/leave', data),
  update: (id, data) => api.put(`/api/hr/leave/${id}`, data),
  calendar: (params) => api.get('/api/hr/leave/calendar', { params }),
};

// Performance Reviews
export const reviewsAPI = {
  list: (params) => api.get('/api/hr/reviews', { params }),
  create: (data) => api.post('/api/hr/reviews', data),
  update: (id, data) => api.put(`/api/hr/reviews/${id}`, data),
};

// Skills & Training
export const skillsAPI = {
  matrix: () => api.get('/api/hr/skills'),
  training: () => api.get('/api/hr/training'),
  addTraining: (data) => api.post('/api/hr/training', data),
};

// Freelancers
export const freelancersAPI = {
  list: () => api.get('/api/hr/freelancers'),
  create: (data) => api.post('/api/hr/freelancers', data),
  update: (id, data) => api.put(`/api/hr/freelancers/${id}`, data),
  delete: (id) => api.delete(`/api/hr/freelancers/${id}`),
};

// Subscriptions V2
export const subscriptionTiersAPI = {
  tiers: () => api.get('/api/subscriptions/tiers'),
  userTier: () => api.get('/api/subscriptions/user-tier'),
  // Staff-only: { user_id, tier_id }. Readers pay via Stripe or the App Store.
  upgrade: (data) => api.post('/api/subscriptions/upgrade', data),
  checkAccess: (articleId) => api.get(`/api/articles/${articleId}/access`),
};

// Recommendations
export const recommendationsAPI = {
  get: (params) => api.get('/api/recommendations', { params }),
  personalized: (params) => api.get('/api/recommendations/personalized', { params }),
};

// Authors
export const authorsAPI = {
  list: (params) => api.get('/api/authors', { params }),
  get: (id) => api.get(`/api/authors/${id}`),
  articles: (id, params) => api.get(`/api/authors/${id}/articles`, { params }),
};

// Topics
export const topicsAPI = {
  get: (tag, params) => api.get(`/api/topics/${tag}`, { params }),
};

// SEO
export const seoAPI = {
  sitemap: () => api.get('/api/seo/sitemap'),
  scores: (params) => api.get('/api/seo/scores', { params }),
};

// Scheduling
export const schedulingAPI = {
  list: () => api.get('/api/scheduled'),
  checkAndPublish: () => api.post('/api/scheduled/check'),
};

// Wire Services (full ingestion system)
export const wireAPI = {
  list: () => api.get('/api/wire/feeds'),
  create: (data) => api.post('/api/wire/feeds', data),
  update: (id, data) => api.put(`/api/wire/feeds/${id}`, data),
  delete: (id) => api.delete(`/api/wire/feeds/${id}`),
  get: (id) => api.get(`/api/wire/feeds/${id}`),
  poll: (id) => api.post(`/api/wire/feeds/${id}/poll`),
  pollAll: () => api.post('/api/wire/poll-all'),
  stats: () => api.get('/api/wire/stats'),
  listItems: (params) => api.get('/api/wire/items', { params }),
  importItem: (itemId) => api.post(`/api/wire/items/${itemId}/import`),
  discardItem: (itemId) => api.post(`/api/wire/items/${itemId}/discard`),
  getItem: (itemId) => api.get(`/api/wire/items/${itemId}`),
};

// Social Publishing (OAuth flows)
export const socialPublishAPI = {
  connect: (provider) => api.get(`/api/social/connect/${provider}`),
  callback: (provider, params) => api.get(`/api/social/callback/${provider}`, { params }),
  disconnect: (provider) => api.delete(`/api/social/disconnect/${provider}`),
  listProviders: () => api.get('/api/social/providers'),
  publish: (articleId, data) => api.post(`/api/social/publish/${articleId}`, data),
  history: (params) => api.get('/api/social/publish-history', { params }),
};

// Planning
export const planningAPI = {
  diary: (params) => api.get('/api/planning/diary', { params }),
  addEntry: (data) => api.post('/api/planning/diary', data),
  deleteEntry: (id) => api.delete(`/api/planning/diary/${id}`),
};

// Commissions
export const commissionsAPI = {
  list: (params) => api.get('/api/commissions', { params }),
  create: (data) => api.post('/api/commissions', data),
  update: (id, data) => api.put(`/api/commissions/${id}`, data),
};

// ========== V3 APIs ==========

// Expanded RBAC
export const rolesAPI = {
  list: () => api.get('/api/roles'),
};

// Hierarchical Categories
export const categoryTreeAPI = {
  tree: () => api.get('/api/categories/tree'),
  setParent: (id, parentId) => api.put(`/api/categories/${id}/parent`, { parent_id: parentId }),
};

// Article Slugs
export const slugsAPI = {
  set: (articleId, data) => api.put(`/api/articles/${articleId}/slug`, data),
  getBySlug: (slug) => api.get(`/api/articles/by-slug/${slug}`),
};

// Content Series
export const seriesAPI = {
  list: () => api.get('/api/series'),
  get: (id) => api.get(`/api/series/${id}`),
  create: (data) => api.post('/api/series', data),
  update: (id, data) => api.put(`/api/series/${id}`, data),
  delete: (id) => api.delete(`/api/series/${id}`),
  manageArticles: (id, data) => api.patch(`/api/series/${id}/articles`, data),
};

// Controlled Vocabulary
export const vocabularyAPI = {
  list: (params) => api.get('/api/vocabulary', { params }),
  suggest: (q) => api.get('/api/vocabulary/suggest', { params: { q } }),
  create: (data) => api.post('/api/vocabulary', data),
  update: (id, data) => api.put(`/api/vocabulary/${id}`, data),
  delete: (id) => api.delete(`/api/vocabulary/${id}`),
};

// Inline Annotations
export const annotationsAPI = {
  list: (articleId) => api.get(`/api/articles/${articleId}/annotations`),
  create: (articleId, data) => api.post(`/api/articles/${articleId}/annotations`, data),
  update: (annotationId, data) => api.put(`/api/annotations/${annotationId}`, data),
  delete: (annotationId) => api.delete(`/api/annotations/${annotationId}`),
};

// Content Expiry
export const expiryAPI = {
  check: () => api.get('/api/articles/expiry/check'),
  archiveExpired: () => api.post('/api/articles/expiry/archive'),
};

// AI Moderation
export const moderationAPI = {
  checkComment: (data) => api.post('/api/moderation/check-comment', data),
  bulkCheck: () => api.post('/api/moderation/bulk-check'),
};

// AI Tools (enhanced)
export const aiToolsAPI = {
  generate: (data) => api.post('/api/ai/generate', data),
  summarize: (data) => api.post('/api/ai/summarize', data),
  categorize: (data) => api.post('/api/ai/categorize', data),
  autoTag: (data) => api.post('/api/ai/auto-tag', data),
  generateSEO: (data) => api.post('/api/ai/generate-seo', data),
};

// Schema.org
export const schemaAPI = {
  article: (id) => api.get(`/api/schema/article/${id}`),
};

// GDPR
export const gdprAPI = {
  exportData: () => api.post('/api/gdpr/export-data'),
  deleteRequest: (data) => api.post('/api/gdpr/delete-request', data),
  listDeletionRequests: () => api.get('/api/gdpr/deletion-requests'),
  getConsent: () => api.get('/api/gdpr/consent'),
  updateConsent: (data) => api.put('/api/gdpr/consent', data),
  retentionPolicies: () => api.get('/api/gdpr/data-retention'),
};

// Support Tickets & FAQ
export const supportAPI = {
  listTickets: (params) => api.get('/api/support/tickets', { params }),
  createTicket: (data) => api.post('/api/support/tickets', data),
  updateTicket: (id, data) => api.put(`/api/support/tickets/${id}`, data),
  listFAQ: () => api.get('/api/support/faq'),
  createFAQ: (data) => api.post('/api/support/faq', data),
  updateFAQ: (id, data) => api.put(`/api/support/faq/${id}`, data),
  deleteFAQ: (id) => api.delete(`/api/support/faq/${id}`),
};

// Recruitment
export const recruitmentAPI = {
  listJobs: (params) => api.get('/api/recruitment/jobs', { params }),
  createJob: (data) => api.post('/api/recruitment/jobs', data),
  updateJob: (id, data) => api.put(`/api/recruitment/jobs/${id}`, data),
  listApplications: (params) => api.get('/api/recruitment/applications', { params }),
  apply: (data) => api.post('/api/recruitment/apply', data),
};

// Finance
export const financeAPI = {
  overview: () => api.get('/api/finance/overview'),
  costCenters: () => api.get('/api/finance/cost-centers'),
};

// Marketing
export const marketingAPI = {
  listCampaigns: (params) => api.get('/api/marketing/campaigns', { params }),
  createCampaign: (data) => api.post('/api/marketing/campaigns', data),
};

// Org Chart
export const orgChartAPI = {
  get: () => api.get('/api/hr/org-chart'),
};

// Shifts
export const shiftsAPI = {
  list: (params) => api.get('/api/hr/shifts', { params }),
  create: (data) => api.post('/api/hr/shifts', data),
  delete: (id) => api.delete(`/api/hr/shifts/${id}`),
};

// HR Cases
export const hrCasesAPI = {
  list: () => api.get('/api/hr/cases'),
  create: (data) => api.post('/api/hr/cases', data),
};

// Compensation
export const compensationAPI = {
  overview: () => api.get('/api/hr/compensation'),
};

// HR Analytics
export const hrAnalyticsAPI = {
  get: () => api.get('/api/hr/analytics'),
};

// UGC
export const ugcAPI = {
  list: (params) => api.get('/api/ugc/submissions', { params }),
  submit: (data) => api.post('/api/ugc/submit', data),
  review: (id, data) => api.put(`/api/ugc/submissions/${id}`, data),
};

// Quizzes
export const quizzesAPI = {
  list: (params) => api.get('/api/quizzes', { params }),
  create: (data) => api.post('/api/quizzes', data),
  attempt: (id, data) => api.post(`/api/quizzes/${id}/attempt`, data),
};

// Gamification
export const gamificationAPI = {
  profile: () => api.get('/api/gamification/profile'),
  action: (data) => api.post('/api/gamification/action', data),
  leaderboard: (params) => api.get('/api/gamification/leaderboard', { params }),
};

// Surveys
export const surveysAPI = {
  list: (params) => api.get('/api/surveys', { params }),
  create: (data) => api.post('/api/surveys', data),
  respond: (id, data) => api.post(`/api/surveys/${id}/respond`, data),
};

// Equipment
export const equipmentAPI = {
  list: () => api.get('/api/equipment'),
  create: (data) => api.post('/api/equipment', data),
  update: (id, data) => api.put(`/api/equipment/${id}`, data),
};

// Sources / Contacts
export const sourcesAPI = {
  list: (params) => api.get('/api/sources', { params }),
  create: (data) => api.post('/api/sources', data),
};

// Redirects
export const redirectsAPI = {
  list: () => api.get('/api/redirects'),
  create: (data) => api.post('/api/redirects', data),
  delete: (id) => api.delete(`/api/redirects/${id}`),
};

// Pre-flight Checks
export const preflightAPI = {
  check: (articleId) => api.get(`/api/articles/${articleId}/preflight`),
};

// User Preferences (dark mode, etc.)
export const preferencesAPI = {
  get: () => api.get('/api/preferences'),
  update: (data) => api.put('/api/preferences', data),
};

// Social Sharing
export const shareAPI = {
  getData: (articleId) => api.get(`/api/share/${articleId}`),
};

// A/B Testing
export const abTestAPI = {
  create: (articleId, data) => api.post(`/api/articles/${articleId}/ab-test`, data),
  get: (articleId) => api.get(`/api/articles/${articleId}/ab-test`),
};

// ========== V4 APIs ==========

// Stripe Payments
export const paymentsAPI = {
  checkout: (data) => api.post('/api/payments/checkout', data),
  status: (sessionId) => api.get(`/api/payments/status/${sessionId}`),
  history: () => api.get('/api/payments/history'),
};

// Email / Newsletter Sending
export const emailAPI = {
  sendNewsletter: (data) => api.post('/api/email/send-newsletter', data),
  history: () => api.get('/api/email/history'),
  templates: () => api.get('/api/email/templates'),
};

// Push Notifications
export const pushAPI = {
  subscribe: (data) => api.post('/api/push/subscribe', data),
  unsubscribe: () => api.delete('/api/push/subscribe'),
  send: (data) => api.post('/api/push/send', data),
  history: () => api.get('/api/push/history'),
};

// Collaborative Editing
export const collabAPI = {
  getEditors: (articleId) => api.get(`/api/collab/${articleId}/editors`),
};

// Block Editor
export const blocksAPI = {
  types: () => api.get('/api/blocks/types'),
  get: (articleId) => api.get(`/api/articles/${articleId}/blocks`),
  save: (articleId, data) => api.put(`/api/articles/${articleId}/blocks`, data),
};

// V5: Auto-save, Clone, Comment Close
export const autosaveAPI = {
  save: (articleId, data) => api.post(`/api/articles/${articleId}/autosave`, data),
};
export const cloneAPI = {
  clone: (articleId) => api.post(`/api/articles/${articleId}/clone`),
};
export const commentCloseAPI = {
  close: (articleId, closed = true) => api.post(`/api/articles/${articleId}/close-comments`, { closed }),
};

// V5: AI Extras
export const aiExtrasAPI = {
  translate: (data) => api.post('/api/ai/translate', data),
  caption: (data) => api.post('/api/ai/caption', data),
  socialPost: (data) => api.post('/api/ai/social-post', data),
  entities: (data) => api.post('/api/ai/entities', data),
  // Editorial headline assistant — returns alternative headlines + rationale
  // for the current draft. Distinct from the headline A/B experiment API.
  headlines: (data) => api.post('/api/ai/headlines', data),
};

// Autosave recovery
export const autosaveRecoveryAPI = {
  get: (articleId) => api.get(`/api/articles/${articleId}/autosave`),
};

// V5: Workflow SLA
export const slaAPI = {
  check: (slaHours) => api.get('/api/workflow/sla-check', { params: slaHours ? { sla_hours: slaHours } : undefined }),
  escalate: (slaHours) => api.post('/api/workflow/sla-escalate', null, { params: slaHours ? { sla_hours: slaHours } : undefined }),
};

// V5: File Upload
export const uploadAPI = {
  upload: (data) => api.post('/api/upload', data),
};

// V5: Webhooks
export const webhooksAPI = {
  list: () => api.get('/api/webhooks'),
  create: (data) => api.post('/api/webhooks', data),
  delete: (id) => api.delete(`/api/webhooks/${id}`),
};

// V5: A/B Results
export const abResultsAPI = {
  get: (articleId) => api.get(`/api/articles/${articleId}/ab-results`),
};

// V5: Reading Progress
export const progressAPI = {
  save: (articleId, progress) => api.post(`/api/articles/${articleId}/progress`, { progress }),
  get: (articleId) => api.get(`/api/articles/${articleId}/progress`),
};

// AI V2 — multi-provider
export const aiV2API = {
  providers: () => api.get('/api/ai/providers'),
  moderateComment: (data) => api.post('/api/ai/moderate-comment', data),
  classifyHarmful: (data) => api.post('/api/ai/classify-harmful', data),
  interestProfile: (data) => api.post('/api/ai/interest-profile', data),
  contentGap: (data) => api.post('/api/ai/content-gap', data),
  churnRisk: (userId, data) => api.post(`/api/ai/churn-risk/${userId}`, data),
  generateSEO: (data) => api.post('/api/ai/generate-seo', data),
};

// Social media management
export const socialAPI = {
  listAccounts: () => api.get('/api/social/accounts'),
  addAccount: (data) => api.post('/api/social/accounts', data),
  removeAccount: (id) => api.delete(`/api/social/accounts/${id}`),
  listScheduled: () => api.get('/api/social/scheduled'),
  schedulePost: (data) => api.post('/api/social/scheduled', data),
  updatePost: (id, data) => api.put(`/api/social/scheduled/${id}`, data),
  deletePost: (id) => api.delete(`/api/social/scheduled/${id}`),
  postNow: (data) => api.post('/api/social/post-now', data),
  analytics: (days) => api.get('/api/social/analytics', { params: { days } }),
};

// Session management
export const sessionAPI = {
  record: (data) => api.post('/api/auth/session', data),
  list: () => api.get('/api/auth/sessions'),
  revoke: (id) => api.delete(`/api/auth/sessions/${id}`),
  revokeAll: () => api.delete('/api/auth/sessions'),
};

// Timesheets
export const timesheetAPI = {
  list: (params) => api.get('/api/hr/timesheets', { params }),
  log: (data) => api.post('/api/hr/timesheets', data),
  submit: (id) => api.put(`/api/hr/timesheets/${id}/submit`),
  approve: (id) => api.put(`/api/hr/timesheets/${id}/approve`),
};

// OKRs / Goals
export const goalsAPI = {
  list: (params) => api.get('/api/goals', { params }),
  create: (data) => api.post('/api/goals', data),
  update: (id, data) => api.put(`/api/goals/${id}`, data),
  delete: (id) => api.delete(`/api/goals/${id}`),
};

// 360 Feedback
export const feedbackAPI = {
  listRequests: () => api.get('/api/feedback/requests'),
  createRequest: (data) => api.post('/api/feedback/requests', data),
  respond: (id, data) => api.post(`/api/feedback/respond/${id}`, data),
  summary: (userId) => api.get(`/api/feedback/summary/${userId}`),
};

// Funnel analysis
export const funnelAPI = {
  recordEvent: (data) => api.post('/api/analytics/funnel-event', data),
  getFunnel: (funnel, days) => api.get('/api/analytics/funnel', { params: { funnel, days } }),
};

// Referral system
export const referralAPI = {
  generate: () => api.post('/api/referrals/generate'),
  my: () => api.get('/api/referrals/my'),
  track: (code) => api.post('/api/referrals/track', { code }),
  convert: (code) => api.post('/api/referrals/convert', { code }),
  leaderboard: () => api.get('/api/referrals/leaderboard'),
};

// Competitor monitoring
export const competitorAPI = {
  list: () => api.get('/api/competitors'),
  add: (data) => api.post('/api/competitors', data),
  remove: (id) => api.delete(`/api/competitors/${id}`),
  fetch: (id) => api.post(`/api/competitors/${id}/fetch`),
  getArticles: (id) => api.get(`/api/competitors/${id}/articles`),
};

// Sustainability / ESG
export const sustainabilityAPI = {
  getMetrics: () => api.get('/api/sustainability/metrics'),
  updateMetrics: (data) => api.put('/api/sustainability/metrics', data),
  getReport: () => api.get('/api/sustainability/report'),
};

// Live chat / support
export const chatAPI = {
  listSessions: () => api.get('/api/chat/sessions'),
  createSession: (data) => api.post('/api/chat/sessions', data),
  sendMessage: (id, data) => api.post(`/api/chat/sessions/${id}/message`, data),
  getSession: (id) => api.get(`/api/chat/sessions/${id}`),
  closeSession: (id) => api.put(`/api/chat/sessions/${id}/close`),
};

// Revenue trend
export const revenueTrendAPI = {
  get: (months) => api.get('/api/analytics/revenue-trend', { params: { months } }),
};

// ========== V5 APIs ==========

// GDPR Full Suite
export const gdprV5API = {
  recordConsent: (data) => api.post('/api/gdpr/consent', data),
  getConsent: (sessionId) => api.get(`/api/gdpr/consent/${sessionId}`),
  updateConsent: (sessionId, data) => api.put(`/api/gdpr/consent/${sessionId}`, data),
  submitDSAR: (data) => api.post('/api/gdpr/dsar', data),
  listDSARs: (params) => api.get('/api/gdpr/dsar', { params }),
  processDSAR: (id, data) => api.put(`/api/gdpr/dsar/${id}`, data),
  getRetention: () => api.get('/api/gdpr/data-retention'),
  setRetention: (data) => api.put('/api/gdpr/data-retention', data),
  purgeExpired: () => api.post('/api/gdpr/purge-expired'),
  exportUserData: (userId) => api.get(`/api/gdpr/export-user-data/${userId}`),
};

// SSO / OAuth
export const oauthAPI = {
  getProviders: () => api.get('/api/auth/oauth/providers'),
  googleUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/oauth/google`,
  githubUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/oauth/github`,
  tokenExchange: (data) => api.post('/api/auth/oauth/token-exchange', data),
};

// Extended roles
export const rolesV5API = {
  extended: () => api.get('/api/roles/extended'),
  userPermissions: (userId) => api.get(`/api/roles/user/${userId}/permissions`),
};

// Finance — Invoices, P&L, Expenses
export const invoiceAPI = {
  list: (params) => api.get('/api/finance/invoices', { params }),
  create: (data) => api.post('/api/finance/invoices', data),
  update: (id, data) => api.put(`/api/finance/invoices/${id}`, data),
  htmlUrl: (id) => `${process.env.REACT_APP_BACKEND_URL || ''}/api/finance/invoices/${id}/pdf`,
  plReport: (year) => api.get('/api/finance/pl-report', { params: { year } }),
  listExpenses: () => api.get('/api/finance/expenses'),
  createExpense: (data) => api.post('/api/finance/expenses', data),
  approveExpense: (id) => api.put(`/api/finance/expenses/${id}/approve`),
};

// HR — Succession, Compensation, Compliance
export const hrV5API = {
  listSuccession: () => api.get('/api/hr/succession'),
  createSuccession: (data) => api.post('/api/hr/succession', data),
  updateSuccession: (id, data) => api.put(`/api/hr/succession/${id}`, data),
  getCompensation: () => api.get('/api/hr/compensation-data'),
  setCompensation: (userId, data) => api.put(`/api/hr/compensation-data/${userId}`, data),
  complianceReport: () => api.get('/api/hr/compliance-report'),
};

// Live data feeds
export const liveDataAPI = {
  weather: (city) => api.get('/api/live-data/weather', { params: { city } }),
  markets: () => api.get('/api/live-data/markets'),
  elections: () => api.get('/api/live-data/election-tracker'),
  sports: () => api.get('/api/live-data/sports'),
};

// Field operations
export const fieldOpsAPI = {
  checkin: (data) => api.post('/api/field/checkin', data),
  listCheckins: (activeOnly) => api.get('/api/field/checkins', { params: { active_only: activeOnly } }),
  submitStory: (data) => api.post('/api/field/story-submit', data),
};

// Landing pages
export const landingPagesAPI = {
  list: () => api.get('/api/landing-pages'),
  create: (data) => api.post('/api/landing-pages', data),
  getBySlug: (slug) => api.get(`/api/landing-pages/${slug}`),
  update: (id, data) => api.put(`/api/landing-pages/${id}`, data),
  trackConversion: (id) => api.post(`/api/landing-pages/${id}/convert`),
};

// Attribution
export const attributionAPI = {
  track: (data) => api.post('/api/attribution/track', data),
  report: (days) => api.get('/api/attribution/report', { params: { days } }),
};

// Email V5 (real delivery)
export const emailV5API = {
  send: (data) => api.post('/api/email/send', data),
  bulkSend: (data) => api.post('/api/email/bulk-send', data),
  getConfig: () => api.get('/api/email/config'),
};

// Data Visualization
export const vizAPI = {
  types: () => api.get('/api/viz/types'),
  create: (data) => api.post('/api/viz/create', data),
  get: (id) => api.get(`/api/viz/${id}`),
  forArticle: (articleId) => api.get(`/api/viz/article/${articleId}`),
};

// Accessibility
export const accessibilityAPI = {
  check: (data) => api.post('/api/accessibility/check', data),
};

// Translation Memory
export const translationMemoryAPI = {
  add: (data) => api.post('/api/translation-memory', data),
  search: (q, targetLang, sourceLang) => api.get('/api/translation-memory/search', { params: { q, target_lang: targetLang, source_lang: sourceLang } }),
};

// Health monitoring
export const healthAPI = {
  detailed: () => api.get('/api/health/detailed'),
  metrics: () => api.get('/api/health/metrics'),
};

// Live data
export const liveDataFeedAPI = liveDataAPI;

// ========== V6 APIs ==========

// OIDC / SSO
export const oidcAPI = {
  config: () => api.get('/api/auth/oidc/config'),
  loginUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/oidc/login`,
};

// Payroll
export const payrollAPI = {
  listRuns: () => api.get('/api/payroll/runs'),
  createRun: (data) => api.post('/api/payroll/runs', data),
  payslipUrl: (userId, period) => `${process.env.REACT_APP_BACKEND_URL || ''}/api/payroll/payslip/${userId}/${period}`,
};

// Programmatic Ads
export const programmaticAdsAPI = {
  getConfig: () => api.get('/api/ads/programmatic/config'),
  updateConfig: (data) => api.put('/api/ads/programmatic/config', data),
  getTag: (slotId) => api.get(`/api/ads/programmatic/tag/${slotId}`),
  contextualTargeting: (data) => api.post('/api/ads/targeting/contextual', data),
};

// Video transcoding & transcripts
export const mediaProcessAPI = {
  transcode: (data) => api.post('/api/media/transcode', data),
  getTranscodeStatus: (jobId) => api.get(`/api/media/transcode/${jobId}`),
  transcript: (data) => api.post('/api/media/transcript', data),
};

// E-Commerce
export const ecommerceAPI = {
  listProducts: () => api.get('/api/ecommerce/products'),
  createProduct: (data) => api.post('/api/ecommerce/products', data),
  updateProduct: (id, data) => api.put(`/api/ecommerce/products/${id}`, data),
  createOrder: (data) => api.post('/api/ecommerce/orders', data),
  listOrders: () => api.get('/api/ecommerce/orders'),
  updateOrderStatus: (id, status) => api.put(`/api/ecommerce/orders/${id}/status`, { status }),
};

// CRDT Collaborative editing
export const crdtAPI = {
  submitOp: (articleId, data) => api.post(`/api/collab/${articleId}/op`, data),
  getOps: (articleId, sinceRevision) => api.get(`/api/collab/${articleId}/ops`, { params: { since_revision: sinceRevision } }),
  clear: (articleId) => api.delete(`/api/collab/${articleId}`),
};

// Carbon / Sustainability extended
export const carbonAPI = {
  intensity: (zone) => api.get('/api/sustainability/carbon-intensity', { params: { zone } }),
  estimate: (data) => api.post('/api/sustainability/carbon-estimate', data),
};

// i18n strings
export const i18nStringsAPI = {
  get: (lang) => api.get(`/api/i18n/strings/${lang}`),
  update: (lang, data) => api.put(`/api/i18n/strings/${lang}`, data),
  rtlLanguages: () => api.get('/api/i18n/rtl-languages'),
};

// System test suite
export const systemTestAPI = {
  run: () => api.get('/api/system/test-suite'),
  customMetrics: () => api.get('/api/metrics/custom'),
};

// Admin seed management
export const seedAPI = {
  status: () => api.get('/api/admin/seed/status'),
  run: (force) => api.post('/api/admin/seed', { force: !!force }),
};

// GA4 server-side
export const ga4API = {
  event: (data) => api.post('/api/analytics/ga4/event', data),
  pageview: (data) => api.post('/api/analytics/ga4/pageview', data),
};

// Email template engine
export const emailTemplateAPI = {
  preview: (data) => api.post('/api/email/template-preview', data),
  sendTransactional: (data) => api.post('/api/email/send-transactional', data),
};

// Stripe enhanced
export const stripeAPI = {
  prices: () => api.get('/api/payments/prices'),
  checkout: (data) => api.post('/api/payments/checkout', data),
  status: (sessionId) => api.get(`/api/payments/status/${sessionId}`),
  portal: (data) => api.post('/api/payments/portal', data),
};

// Ad user targeting
export const adTargetAPI = {
  userInterest: (data) => api.post('/api/ads/targeting/user-interest', data),
  userTag: (slotId) => api.get('/api/ads/programmatic/user-tag', { params: { slot_id: slotId } }),
};

// Connected Platforms
export const platformAPI = {
  carplay: () => api.get('/api/platform/carplay'),
  wearable: (limit) => api.get('/api/platform/wearable', { params: { limit } }),
  voiceManifest: () => api.get('/api/platform/voice-manifest'),
  smartTVUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/platform/smart-tv`,
};

// Data Infrastructure / Analytics Warehouse
export const warehouseAPI = {
  stream: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/stream`,
  warehouse: (days, granularity) => api.get('/api/analytics/warehouse', { params: { days, granularity } }),
  exportCsv: (days, granularity) => `${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/export-csv?days=${days}&granularity=${granularity}`,
  pipelineStatus: () => api.get('/api/analytics/pipeline-status'),
  ingestBatch: (events) => api.post('/api/analytics/ingest-batch', { events }),
};

// DR & Observability
export const drAPI = {
  createBackup: () => api.post('/api/system/backup', {}),
  listBackups: () => api.get('/api/system/backups'),
  restore: (id) => api.post(`/api/system/restore/${id}`),
  multiRegionHealth: () => api.get('/api/system/health/multi-region'),
  observabilityConfig: () => api.get('/api/system/observability-config'),
  drRunbook: () => api.get('/api/system/dr-runbook'),
};

// Text-to-Speech
export const ttsAPI = {
  generate: (articleId, voice) => api.post(`/api/articles/${articleId}/tts`, { voice }),
};

// Atom Feed
export const atomAPI = {
  feedUrl: (category) => `${process.env.REACT_APP_BACKEND_URL || ''}/api/atom${category ? `?category=${category}` : ''}`,
};

// iCal Calendar Export
export const icalAPI = {
  downloadUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/editorial/calendar/ical`,
};

// Contract Management
export const contractAPI = {
  list: (params) => api.get('/api/legal/contracts', { params }),
  create: (data) => api.post('/api/legal/contracts', data),
  update: (id, data) => api.put(`/api/legal/contracts/${id}`, data),
  delete: (id) => api.delete(`/api/legal/contracts/${id}`),
};

// LMS
export const lmsAPI = {
  listCourses: () => api.get('/api/lms/courses'),
  createCourse: (data) => api.post('/api/lms/courses', data),
  updateCourse: (id, data) => api.put(`/api/lms/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/api/lms/courses/${id}`),
  enroll: (courseId) => api.post(`/api/lms/courses/${courseId}/enroll`),
  updateProgress: (courseId, pct) => api.put(`/api/lms/courses/${courseId}/progress`, { progress_pct: pct }),
  myCourses: () => api.get('/api/lms/my-courses'),
};

// Multi-resource calendar
export const resourceCalendarAPI = {
  assignResources: (eventId, resources) => api.post(`/api/editorial/calendar/${eventId}/resources`, { resources }),
  getResources: (eventId) => api.get(`/api/editorial/calendar/${eventId}/resources`),
  checkAvailability: (date) => api.get('/api/editorial/resource-availability', { params: { date } }),
};

// WAF / IP Security
export const wafAPI = {
  getBlacklist: () => api.get('/api/security/ip-blacklist'),
  updateBlacklist: (data) => api.put('/api/security/ip-blacklist', data),
  blacklistIP: (ip, reason) => api.post(`/api/security/ip-blacklist/${ip}`, { reason }),
};

// Streaming
export const streamingAPI = {
  create: (data) => api.post('/api/streaming/create', data),
  list: () => api.get('/api/streaming'),
  updateStatus: (id, status) => api.put(`/api/streaming/${id}/status`, { status }),
};

// AI accountability
export const aiGovernanceAPI = {
  listDecisions: () => api.get('/api/governance/ai-decisions'),
  logDecision: (data) => api.post('/api/governance/ai-decisions', data),
  report: (days) => api.get('/api/governance/ai-decisions/report', { params: { days } }),
};

// Grafana monitoring
export const monitoringAPI = {
  grafanaConfig: () => api.get('/api/monitoring/grafana-config'),
  grafanaDashboard: () => api.get('/api/monitoring/grafana-dashboard'),
};

// ========== V7 APIs ==========

// Tamper-evident audit chain
export const auditChainAPI = {
  verify: (limit) => api.get('/api/audit/verify', { params: { limit } }),
  append: (data) => api.post('/api/audit/append', data),
  exportCsvUrl: (since, until) => {
    const qs = new URLSearchParams();
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    const sep = qs.toString() ? '?' : '';
    return `/api/audit/export.csv${sep}${qs.toString()}`;
  },
  exportJsonUrl: (since, until) => {
    const qs = new URLSearchParams();
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    const sep = qs.toString() ? '?' : '';
    return `/api/audit/export.json${sep}${qs.toString()}`;
  },
  download: async (format = 'csv', { since, until } = {}) => {
    const url = format === 'json'
      ? auditChainAPI.exportJsonUrl(since, until)
      : auditChainAPI.exportCsvUrl(since, until);
    const res = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: res.headers['content-type'] });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `audit-${new Date().toISOString().slice(0, 10)}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  },
};

// Push notification device registry (admin views)
export const pushDevicesAPI = {
  myDevices: () => api.get('/api/notifications/devices'),
  sendToUser: (userId, data) => api.post(`/api/notifications/send-to-user/${userId}`, data),
  broadcast: (data) => api.post('/api/notifications/broadcast', data),
};

// SAML/OIDC SSO config
export const ssoAPI = {
  getConfig: () => api.get('/api/auth/sso/config'),
  upsertConfig: (provider, data) => api.put(`/api/auth/sso/config/${provider}`, data),
  oidcLoginUrl: (redirectTo = '/') =>
    `/api/auth/sso/oidc/login?redirect_to=${encodeURIComponent(redirectTo)}`,
};

// Data retention + secrets rotation
export const governanceAPI = {
  retentionDryRun: () => api.get('/api/governance/retention/dry-run'),
  retentionRun: () => api.post('/api/governance/retention/run', { confirm: true }),
  secretsRotation: () => api.get('/api/governance/secrets-rotation'),
  markSecretRotated: (name) => api.post(`/api/governance/secrets-rotation/${encodeURIComponent(name)}`),
  aiDecisionsFeed: (limit) => api.get('/api/governance/ai-decisions/feed', { params: { limit } }),
  aiDecisionsSummary: (days) => api.get('/api/governance/ai-decisions/summary', { params: { days } }),
};

// Approval chain (parallel/sequential)
export const approvalChainAPI = {
  get: (articleId) => api.get(`/api/articles/${articleId}/approval-chain`),
  set: (articleId, chain) => api.put(`/api/articles/${articleId}/approval-chain`, { chain }),
  approveAs: (articleId, role, note) =>
    api.post(`/api/articles/${articleId}/approve-as-role`, { role, note }),
};

// Corporate / multi-seat subscriptions
export const corpSubsAPI = {
  create: (data) => api.post('/api/subscriptions/corporate', data),
  get: (accountId) => api.get(`/api/subscriptions/corporate/${accountId}`),
  addSeat: (accountId, email) => api.post(`/api/subscriptions/corporate/${accountId}/seats`, { email }),
  removeSeat: (accountId, email) => api.delete(`/api/subscriptions/corporate/${accountId}/seats/${encodeURIComponent(email)}`),
  mine: () => api.get('/api/subscriptions/corporate/mine'),
};

// Entitlements
export const entitlementsAPI = {
  list: () => api.get('/api/entitlements'),
  update: (tier, data) => api.put(`/api/entitlements/${tier}`, data),
  me: () => api.get('/api/entitlements/me'),
};

// AI helpers
export const aiV7API = {
  autoTag: (articleId) => api.post(`/api/ai/auto-tag-article/${articleId}`),
  altText: (data) => api.post('/api/ai/alt-text', data),
  toxicity: (data) => api.post('/api/ai/toxicity', data),
};

// Headline A/B experiments
export const headlineExpAPI = {
  create: (articleId, variants) => api.post(`/api/articles/${articleId}/headline-experiment`, { variants }),
  get: (articleId) => api.get(`/api/articles/${articleId}/headline-experiment`),
  recordEvent: (articleId, variantId, event) =>
    api.post(`/api/articles/${articleId}/headline-experiment/event`, { variant_id: variantId, event }),
  list: () => api.get('/api/headline-experiments'),
};

// Paywall check
export const paywallAPI = {
  check: (articleId) => api.get(`/api/paywall/check/${articleId}`),
};

// Observability
export const observabilityAPI = {
  heartbeat: (data) => api.post('/api/observability/heartbeat', data),
  heartbeats: (kind) => api.get('/api/observability/heartbeats', { params: { kind } }),
  correlation: (id) => api.get(`/api/observability/correlation/${encodeURIComponent(id)}`),
};
